/** @file node.mirrorPair: pairs two nodes as left/right twins both ways, or unpairs a node. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { readInput } from '../validate/readInput';
import { requireNode } from './requireNode';

/**
 * `node.mirrorPair { node, twin }` → `{}`: the node's old partner is unpaired; a twin paired with another node is
 * `NODE_DUPLICATE_MIRROR`; `twin: null` unpairs both. Inverse P.
 */
export const nodeMirrorPairCommand: CommandDef<'node.mirrorPair'> = {
  name: 'node.mirrorPair',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['node', 'twin']);
    return { node: r.str('node'), twin: r.nullable('twin', r.str) };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const node = requireNode(p, input.node, '/input/node');
    const twin = input.twin === null ? null : requireNode(p, input.twin, '/input/twin');
    if (input.twin === input.node) throwError(ERROR_CODES.VALIDATION, { node: input.node }, { path: '/input/twin', hint: 'a node is not its own twin' });
    if (twin?.mirror !== undefined && twin.mirror !== input.node) {
      throwError(ERROR_CODES.NODE_DUPLICATE_MIRROR, { twin: input.twin, other: twin.mirror }, { path: '/input/twin', hint: `node.mirrorPair ${String(input.twin)} null first` });
    }
    const inverse = puppetInverse(draft);
    const old = node.mirror === undefined ? undefined : p.nodes[node.mirror];
    if (old !== undefined) delete old.mirror;
    delete node.mirror;
    if (twin !== null && input.twin !== null) {
      node.mirror = input.twin;
      twin.mirror = input.node;
    }
    checkPuppet(draft, ctx);
    return { result: {}, warnings: [], inverse };
  },
};
